import { AddIcon, DeleteIcon, TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
    Button,
    Center,
    Checkbox,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    Table,
    TableContainer,
    Tbody,
    Td,
    Thead,
    Tr,
    useToast,
} from '@chakra-ui/react';
import { useCallback } from 'react';
import usePipelineStore from '../../stores/pipeline-store';
import useRuntimeStore from '../../stores/runtime-store';

export const isKeyLegal = (key: string) => {
    const regex = RegExp(/^[a-z0-9_]*$/, 'g');
    const illegal1 = RegExp(/^((?!___).)*$/, 'g'); // don't allow ___ (three under scores), it's reserved for server purposes.

    return regex.test(key) && illegal1.test(key);
};

type VarRowProp = {
    varId: string;
};
function VarRow({ varId }: VarRowProp) {
    const { pipeline, updateVar, setPipeline }: { pipeline: PipelineJson; updateVar: any; setPipeline: any } =
        usePipelineStore((state: any) => state);
    const toast = useToast();

    const deleteVar = useCallback(() => {
        const stepsStr = JSON.stringify(pipeline.steps);
        const accurenceInSteps = stepsStr.split(varId).length - 1;
        if (
            accurenceInSteps == 0 ||
            window.confirm(
                `This var is being used times ${accurenceInSteps} places.\nAre you sure you want to delete it?`,
            ) == true
        ) {
            let newPipeline = JSON.parse(JSON.stringify(pipeline));
            newPipeline.steps = JSON.parse(stepsStr.replaceAll(varId, ''));

            const index = newPipeline.editorSettings.varsOrder.indexOf(varId);
            newPipeline.editorSettings.varsOrder.splice(index, 1);
            newPipeline.vars[varId] = undefined;
            setPipeline(newPipeline);
        }
    }, [pipeline]);

    const setVarProp = useCallback(
        (propName, propValue) => {
            let newPipeline = JSON.parse(JSON.stringify(pipeline));
            newPipeline.vars[varId][propName] = propValue;
            setPipeline(newPipeline);
        },
        [pipeline, setPipeline],
    );

    return (
        <Tr border={'none'}>
            <Td border={'none'}>
                <Flex>
                    <Button
                        backgroundColor="white"
                        _hover={{ color: '#ffcccb' }}
                        _focus={{
                            backgroundColor: 'white',
                        }}
                        _active={{
                            backgroundColor: 'white',
                        }}
                        color={'grey'}
                        leftIcon={<DeleteIcon />}
                        onClick={deleteVar}
                    />
                    <Flex>
                        <Button
                            backgroundColor="white"
                            w={'25px'}
                            _hover={{ color: '#ffcccb' }}
                            _focus={{
                                backgroundColor: 'white',
                            }}
                            _active={{
                                backgroundColor: 'white',
                            }}
                            color={'grey'}
                            leftIcon={<TriangleDownIcon w={'25px'} />}
                            onClick={() => {
                                let newPipeline = JSON.parse(JSON.stringify(pipeline)) as PipelineJson;
                                const index = newPipeline.editorSettings.varsOrder.indexOf(varId);

                                if (index < newPipeline.editorSettings.varsOrder.length - 1) {
                                    newPipeline.editorSettings.varsOrder.splice(index, 1);
                                    newPipeline.editorSettings.varsOrder.splice(index + 1, 0, varId);
                                }
                                setPipeline(newPipeline);
                            }}
                        />
                        <Button
                            backgroundColor="white"
                            w={'25px'}
                            _hover={{ color: '#ccccbb' }}
                            _focus={{
                                backgroundColor: 'white',
                            }}
                            _active={{
                                backgroundColor: 'white',
                            }}
                            color={'grey'}
                            leftIcon={<TriangleUpIcon w={'25px'} />}
                            onClick={() => {
                                let newPipeline = JSON.parse(JSON.stringify(pipeline)) as PipelineJson;
                                const index = newPipeline.editorSettings.varsOrder.indexOf(varId);

                                if (index > 0) {
                                    newPipeline.editorSettings.varsOrder.splice(index, 1);
                                    newPipeline.editorSettings.varsOrder.splice(index - 1, 0, varId);
                                }
                                setPipeline(newPipeline);
                            }}
                        />
                    </Flex>

                    <Select
                        value={pipeline.vars[varId].type}
                        width={120}
                        color={pipeline.vars[varId].type == '' ? 'grey' : 'black'}
                        backgroundColor="white"
                        onChange={({ target }: any) => {
                            let newPipeline = JSON.parse(JSON.stringify(pipeline));
                            newPipeline.vars[varId]['type'] = target.value;
                            if (target.value == 'STRING') {
                                newPipeline.vars[varId]['defaultValue'] = '';
                            }
                            if (target.value == 'NUMBER') {
                                newPipeline.vars[varId]['defaultValue'] = 0;
                            }
                            if (target.value == 'BOOLEAN') {
                                newPipeline.vars[varId]['defaultValue'] = false;
                            }
                            if (target.value == 'REF') {
                                newPipeline.vars[varId]['defaultValue'] = '';
                            }
                            setPipeline(newPipeline);
                        }}
                    >
                        <option value="" disabled={true}>
                            Select Var
                        </option>
                        <option value="STRING">String</option>
                        <option value="NUMBER">Number</option>
                        <option value="BOOLEAN">Boolean</option>
                        <option value="REF">Ref</option>
                    </Select>
                    {pipeline.vars[varId].type !== 'REF' && (
                        <Flex ml={5}>
                            <Checkbox
                                isChecked={pipeline.vars[varId].isLaunchVar}
                                onChange={({ target: { checked } }: any) => {
                                    setVarProp('isLaunchVar', checked);
                                }}
                            >
                                🚀
                            </Checkbox>
                            <Checkbox
                                ml={2}
                                isChecked={pipeline.vars[varId].isPublicVar}
                                onChange={({ target: { checked } }: any) => {
                                    setVarProp('isPublicVar', checked);
                                }}
                            >
                                👀
                            </Checkbox>
                        </Flex>
                    )}
                </Flex>
            </Td>
            <Td border={'none'}>
                <Input
                    placeholder="key"
                    width={400}
                    value={pipeline.vars[varId].name}
                    onChange={({ target }: any) => {
                        if (!isKeyLegal(target.value) && target.value !== '') {
                            toast({
                                title: `Must contains only numbers, lowercase letters, and "_'`,
                                position: 'bottom',
                                status: 'error',
                                isClosable: true,
                            });
                        } else {
                            toast.closeAll();
                            setVarProp('name', target.value);
                        }
                    }}
                />
            </Td>
            <Td border={'none'}>
                {pipeline.vars[varId].type == 'STRING' && (
                    <Input
                        placeholder="Default value"
                        value={pipeline.vars[varId].defaultValue}
                        width={180}
                        ml={2}
                        mr={2}
                        onChange={({ target }: any) => {
                            setVarProp('defaultValue', target.value);
                        }}
                    />
                )}
                {pipeline.vars[varId].type == 'NUMBER' && (
                    <NumberInput
                        bg="white"
                        width={180}
                        ml={2}
                        value={pipeline.vars[varId].defaultValue}
                        onChange={(value: any) => {
                            setVarProp('defaultValue', parseInt(value));
                        }}
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                )}
                {pipeline.vars[varId].type == 'BOOLEAN' && (
                    <Select
                        width={200}
                        ml={2}
                        value={`${pipeline.vars[varId].defaultValue}`}
                        onChange={({ target: { value } }: any) => {
                            setVarProp('defaultValue', value == 'true' ? true : false);
                        }}
                    >
                        <option value="true">True</option>
                        <option value="false">False</option>
                    </Select>
                )}
                {(pipeline.vars[varId].type == '' || pipeline.vars[varId].type == 'REF') && (
                    <Input
                        placeholder=""
                        width={200}
                        ml={2}
                        disabled={true}
                        borderWidth={0}
                        value={pipeline.vars[varId].defaultValue}
                        onChange={({ target }: any) => {}}
                    />
                )}
            </Td>
            <Td border={'none'}></Td>
        </Tr>
    );
}

export function VarsModal() {
    const { varsModalIsOpen, setVarsModalIsOpen } = useRuntimeStore((state: any) => state);
    const { pipeline, updateVar, setPipeline }: { pipeline: PipelineJson; updateVar: any; setPipeline: any } =
        usePipelineStore((state: any) => state);

    const addNewVar = useCallback(() => {
        let varKey = '';
        while (!varKey) {
            const randKey = Math.random().toString(36).slice(2, 17);
            if (pipeline.editorSettings.varsOrder.indexOf(randKey) < 0) {
                varKey = randKey;
            }
        }
        let newVar: PipelineVar = {
            defaultValue: '',
            name: '',
            type: '',
            isLaunchVar: false,
            isPublicVar: false,
        };
        let newPipeline = JSON.parse(JSON.stringify(pipeline));
        newPipeline.editorSettings.varsOrder.push(varKey);
        newPipeline.vars[varKey] = newVar;
        setPipeline(newPipeline);
    }, [pipeline, setPipeline]);

    return (
        <Modal
            isOpen={varsModalIsOpen}
            size={'6xl'}
            onClose={() => {
                setVarsModalIsOpen(false);
            }}
            scrollBehavior="inside"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Pipeline Vars</ModalHeader>
                <ModalCloseButton
                    onClick={() => {
                        setVarsModalIsOpen(false);
                    }}
                />
                <ModalBody>
                    <TableContainer overflowX={'hidden'}>
                        <Table border={'none'}>
                            <Thead>
                                <Tr></Tr>
                            </Thead>
                            <Tbody>
                                {pipeline.editorSettings.varsOrder.map((varId: string) => (
                                    <VarRow varId={varId} key={varId} />
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </ModalBody>

                <ModalFooter justifyContent={'center'} marginBottom={3}>
                    <Center>
                        <Button
                            backgroundColor="white"
                            _hover={{
                                backgroundColor: 'white',
                                color: 'black',
                            }}
                            _focus={{ backgroundColor: 'white' }}
                            _active={{ backgroundColor: 'white' }}
                            color={'grey'}
                            leftIcon={<AddIcon />}
                            height="1.75rem"
                            onClick={addNewVar}
                        >
                            Add var
                        </Button>
                    </Center>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
